import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Pencil, Archive, RotateCcw, Tag, Users } from 'lucide-react';
import { listPlans, archivePlan, updatePlan, Plan, money } from './api';
import MembershipForm from './MembershipForm';
import DiscountsPanel from './DiscountsPanel';
import EmptyState from '../../components/EmptyState';
import { Skeleton } from '../../components/Skeleton';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { readCache, writeCache } from '../../services/localCache';

export default function MembershipsPage() {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = user?.role === 'admin';
  // Pinta al instante lo último conocido (caché local, vista sin archivados)
  // y refresca por detrás.
  const [plans, setPlans] = useState<Plan[] | null>(() => readCache<Plan[]>('plans-list') ?? null);
  const [showArchived, setShowArchived] = useState(false);
  const [form, setForm] = useState<{ open: boolean; plan?: Plan | null }>({ open: false });
  // La pestaña vive en la URL para poder mandar el enlace directo: /membresias?tab=descuentos
  const [sp, setSp] = useSearchParams();
  const tab: 'planes' | 'descuentos' = sp.get('tab') === 'descuentos' ? 'descuentos' : 'planes';
  const setTab = (t: 'planes' | 'descuentos') => setSp(t === 'descuentos' ? { tab: t } : {}, { replace: true });

  const firstRun = useRef(true);
  const reload = useCallback(() => {
    if (!firstRun.current) setPlans(null); // solo la 1ª carga se pinta del caché
    firstRun.current = false;
    listPlans(showArchived).then((r) => {
      setPlans(r.data);
      if (!showArchived) writeCache('plans-list', r.data);
    });
  }, [showArchived]);
  useEffect(() => { reload(); }, [reload]);

  async function onArchive(p: Plan) {
    if (!confirm(`¿Archivar el plan "${p.name}"? Dejará de aparecer al asignar mensualidad. Los ${p.student_count} alumnos que lo tienen no se tocan.`)) return;
    try { await archivePlan(p.membership_id); toast.success('Plan archivado'); reload(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo archivar'); }
  }
  async function onRestore(p: Plan) {
    try { await updatePlan(p.membership_id, { deleted: 0 }); toast.success('Plan restaurado'); reload(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo restaurar'); }
  }

  const active = (plans || []).filter((p) => !p.deleted);

  return (
    // Ruta densa (1600px) — el ancho lo gobierna AppLayout, sin tope interno.
    <div>
      <h2 className="mb-1 flex items-center gap-2 font-display text-2xl font-bold text-content"><Tag className="text-brand-blue" size={24} /> Mensualidades</h2>
      <p className="mb-4 text-sm text-content-3">Planes, precios y descuentos del estudio. El precio mensual (menos el descuento) es lo que usa la cobranza.</p>

      {/* Pestañas Planes | Descuentos */}
      <div className="mb-5 inline-flex rounded-lg border border-line bg-surface p-1">
        {([['planes', 'Planes'], ['descuentos', 'Descuentos']] as const).map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition ${tab === k ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2 hover:text-content'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'descuentos' ? <DiscountsPanel /> : (
      <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-content-2">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-[rgb(var(--c-accent-solid))]" />
          Mostrar archivados
        </label>
        {isAdmin && (
          <button onClick={() => setForm({ open: true, plan: null })}
            className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-1.5 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90">
            <Plus size={16} /> Nuevo plan
          </button>
        )}
      </div>

      {!plans ? (
        <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
      ) : active.length === 0 && !showArchived ? (
        <div className="rounded-2xl border border-line bg-surface">
          <EmptyState icon={Tag} title="Sin planes" subtitle="Crea el primer plan de mensualidad." />
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
          {/* Escritorio */}
          <table className="hidden w-full text-sm sm:table">
            <thead>
              <tr className="border-b border-line bg-surface-2 text-left text-xs text-content-3">
                <th className="px-4 py-2 font-medium">Plan</th>
                <th className="px-4 py-2 text-center font-medium">Clases/mes</th>
                <th className="px-4 py-2 text-right font-medium">Mensual</th>
                <th className="px-4 py-2 text-right font-medium">Trimestre</th>
                <th className="px-4 py-2 text-right font-medium">Anual</th>
                <th className="px-4 py-2 text-center font-medium">Alumnas</th>
                {isAdmin && <th className="px-2 py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.membership_id} className={`group border-t border-line transition even:bg-surface-2/40 hover:bg-surface-2 ${p.deleted ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-2">
                    <span className="font-medium text-content">{p.name}</span>
                    {p.code && <span className="ml-2 text-xs text-content-3">{p.code}</span>}
                    {!!p.deleted && <span className="ml-2 rounded bg-content-3/15 px-1.5 py-0.5 text-[10px] font-medium text-content-3">archivado</span>}
                  </td>
                  <td className="px-4 py-2 text-center text-content-2">{p.classes_month ?? '—'}</td>
                  <td className="px-4 py-2 text-right font-semibold text-content">{p.price_month != null ? money(p.price_month) : '—'}</td>
                  <td className="px-4 py-2 text-right text-content-3">{Number(p.price_trimester) > 0 ? money(p.price_trimester) : '—'}</td>
                  <td className="px-4 py-2 text-right text-content-3">{Number(p.price_year) > 0 ? money(p.price_year) : '—'}</td>
                  <td className="px-4 py-2 text-center">
                    <span className="inline-flex items-center gap-1 text-content-2"><Users size={13} className="text-content-3" />{p.student_count}</span>
                  </td>
                  {isAdmin && (
                    <td className="px-2 py-2 text-right">
                      <div className="hover-reveal flex items-center justify-end gap-1 opacity-0 transition group-hover:opacity-100">
                        <button onClick={() => setForm({ open: true, plan: p })} title="Editar" className="rounded p-1 text-content-3 hover:text-brand-blue"><Pencil size={15} /></button>
                        {p.deleted
                          ? <button onClick={() => onRestore(p)} title="Restaurar" className="rounded p-1 text-content-3 hover:text-emerald-500"><RotateCcw size={15} /></button>
                          : <button onClick={() => onArchive(p)} title="Archivar" className="rounded p-1 text-content-3 hover:text-amber-500"><Archive size={15} /></button>}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Móvil */}
          <div className="divide-y divide-line sm:hidden">
            {plans.map((p) => (
              <div key={p.membership_id} className={`px-4 py-3 ${p.deleted ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-content">{p.name}{p.code && <span className="ml-2 text-xs text-content-3">{p.code}</span>}</div>
                    <div className="mt-0.5 text-xs text-content-3">
                      {p.classes_month != null ? `${p.classes_month} clases/mes · ` : ''}<Users size={11} className="inline" /> {p.student_count} alumnos
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-display font-bold text-content">{p.price_month != null ? money(p.price_month) : '—'}</div>
                    <div className="text-[11px] text-content-3">/mes</div>
                  </div>
                </div>
                {isAdmin && (
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => setForm({ open: true, plan: p })} className="flex items-center gap-1 rounded-lg border border-line px-2.5 py-1 text-xs text-content-2"><Pencil size={13} /> Editar</button>
                    {p.deleted
                      ? <button onClick={() => onRestore(p)} className="flex items-center gap-1 rounded-lg border border-line px-2.5 py-1 text-xs text-content-2"><RotateCcw size={13} /> Restaurar</button>
                      : <button onClick={() => onArchive(p)} className="flex items-center gap-1 rounded-lg border border-line px-2.5 py-1 text-xs text-content-2"><Archive size={13} /> Archivar</button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {form.open && <MembershipForm plan={form.plan} onClose={() => setForm({ open: false })} onSaved={() => { setForm({ open: false }); reload(); }} />}
      </>
      )}
    </div>
  );
}
